import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Image as ImageIcon } from "lucide-react";
import { Reveal, Eyebrow, EASE, IMAGES } from "./shared";

const PUB = process.env.PUBLIC_URL || "";
const ARCHIVAL_PHOTO = `${PUB}/archival.png?v=2`;
const PORTRAITS = { YF: `${PUB}/leader-yatin.png?v=2`, RF: `${PUB}/leader-raunak.png` };

const INTRO = [
  "Fortim Ventures is built on more than a business plan—it is built on a legacy of entrepreneurship that has been part of the Union Territory of Dadra & Nagar Haveli and Daman & Diu for over a century.",
  "Since 1924, the Fugro family has contributed to the region through business, hospitality, industry leadership and community initiatives. Across generations, this legacy has been shaped by a commitment to long-term thinking, meaningful relationships and building businesses that create lasting value.",
  "Today, Fortim represents the next chapter of that journey—bringing together generations of regional understanding with a modern perspective on business, technology and growth.",
  "Together, our leadership combines deep local roots with global experience to build ventures that are purposefully designed for DNHDD while creating opportunities that extend far beyond it.",
];

const LEADERS = [
  {
    initials: "YF",
    name: "Yatin Fugro",
    role: "Chairman",
    bio: [
      "Yatin Fugro serves as Chairman of Fortim Ventures, providing strategic guidance shaped by more than three decades of entrepreneurial leadership and a lifetime of experience building businesses in DNHDD.",
      "Widely revered across the Union Territory, Yatin has played a significant role in the region's commercial development, particularly within the hospitality sector. In 1998, he established Diu's first graded hospitality property, helping lay the foundation for the island's modern hospitality industry. Since then, he has successfully developed and operated multiple hospitality ventures recognised for their quality, professionalism, and long-term vision.",
      "Beyond entrepreneurship, Yatin has made meaningful contributions to industry and public institutions. He served as an Independent Director of the Omnibus Industrial Development Corporation (OIDC) for over a decade, helping guide initiatives that supported the economic development of the Union Territory. His extensive understanding of the region's regulatory and commercial landscape continues to provide Fortim with valuable strategic perspective.",
      "He also serves as an Executive Committee Member of the Hotel and Restaurant Association (Western India) (HRAWI), contributing to the advancement of hospitality standards across the region. As President of the Diu Hotels Association, and formerly an Independent Director of Diu Smart City Ltd., he has played an active role in strengthening the region's hospitality ecosystem, supporting tourism-led development, and representing the interests of local businesses.",
      "Beyond business, Yatin remains deeply committed to community development. He is the Chairman of the Amichand Rupchand Trust, which provides free medical assistance and essential healthcare support to economically weaker sections of Diu, and is a founding member of The Society for Welfare of Children with Special Needs (Vatsalya). These initiatives reflect his belief that lasting success is measured not only by commercial achievement, but also by creating meaningful and enduring value for the community.",
    ],
  },
  {
    initials: "RF",
    name: "Raunak Fugro",
    role: "Founder & Managing Director",
    bio: [
      "Raunak Fugro is the Founder and Managing Director of Fortim Ventures, where he leads the company's long-term vision, strategic direction, and business development initiatives.",
      "Before founding Fortim, Raunak spent over three years in investment banking, advising founders, investors, and executive teams on mergers and acquisitions involving technology and AI-driven companies across India, North America, and Europe. Exposure to businesses across a wide range of industries, growth stages, and strategic transactions gave him a first-hand understanding of how enduring companies are built, scaled, and positioned for long-term success.",
      "That experience shaped Fortim's philosophy of combining deep regional understanding with contemporary business thinking. It also reinforced his belief that technology, when thoughtfully applied, enables businesses of every size to operate more efficiently, make better decisions, and compete in an increasingly dynamic marketplace.",
      "Today, Raunak is focused on building the next chapter of the Fugro family's entrepreneurial legacy through Fortim Ventures—building businesses, forging strategic partnerships, and creating long-term value that begins in DNHDD and extends well beyond it.",
      "Raunak holds a Bachelor of Management degree with specializations in Finance and Entrepreneurial Technology from the University of British Columbia's Faculty of Management.",
    ],
  },
];

// Framed photo. Renders an uploaded image when `src` is given, otherwise a
// styled placeholder. `children` allows overlays (e.g. an archival caption).
const PhotoPlaceholder = ({ src, alt = "", initials, caption, className = "", style = {}, children }) => (
  <div className={`relative overflow-hidden rounded-lg ${className}`} style={{ backgroundColor: "#1F1A38", ...style }}>
    {src ? (
      <img src={src} alt={alt} className="absolute inset-0 w-full h-full object-cover" />
    ) : (
      <>
        <span className="absolute top-4 left-4 w-2 h-2 rounded-full" style={{ background: "#F5821F" }} />
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
          {initials ? (
            <span className="text-5xl lg:text-6xl font-bold tracking-brand" style={{ color: "#FAF9F7" }}>{initials}</span>
          ) : (
            <ImageIcon size={30} strokeWidth={1.3} style={{ color: "#6B5CA5" }} />
          )}
          <span className="eyebrow text-[10px]" style={{ color: "#8C8C8C" }}>{caption}</span>
        </div>
      </>
    )}
    {children}
  </div>
);

export const LeadershipPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, []);

  return (
    <main data-testid="leadership-page" style={{ backgroundColor: "#FAF9F7" }}>
      {/* Heading */}
      <section className="pt-32 lg:pt-44 pb-16 lg:pb-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <Reveal>
            <button
              data-testid="leadership-back"
              onClick={() => navigate("/", { state: { scrollTo: "home" } })}
              className="group inline-flex items-center gap-2 text-sm font-medium mb-12 transition-colors duration-300"
              style={{ color: "#8C8C8C" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#3B2C82")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "#8C8C8C")}
            >
              <ArrowLeft size={16} className="transition-transform duration-300 group-hover:-translate-x-1" />
              Back to home
            </button>
          </Reveal>
          <Reveal delay={0.04}><Eyebrow>Leadership</Eyebrow></Reveal>
          <Reveal delay={0.08}>
            <h1 className="font-bold leading-[1.05] text-4xl sm:text-5xl lg:text-6xl max-w-4xl" style={{ color: "#2B2B2B" }}>
              A Legacy That <span style={{ color: "#3B2C82" }}>Continues to Build</span>
            </h1>
          </Reveal>
        </div>
      </section>

      {/* Intro: archival photo left, copy right */}
      <section className="pb-24 lg:pb-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <Reveal>
              <PhotoPlaceholder
                src={ARCHIVAL_PHOTO}
                alt="Archival family photograph of the Fugro family, c. 1924"
                className="w-full"
                style={{ aspectRatio: "1466 / 1073" }}
              >
                <div
                  className="absolute top-4 right-4 z-10 rounded-sm px-3 py-2"
                  style={{ backgroundColor: "rgba(31,26,56,0.72)", backdropFilter: "blur(4px)" }}
                >
                  <p className="text-[10px] sm:text-xs font-medium leading-snug text-white">
                    Shri Keshav Fugro, seated at centre.
                  </p>
                </div>
              </PhotoPlaceholder>
            </Reveal>
            <Reveal delay={0.08} as="p" className="mt-4 text-xs" style={{ color: "#8C8C8C" }}>
              The Fugro family’s presence in the region since 1924.
            </Reveal>
          </div>
          <div className="lg:col-span-7 lg:pt-2 space-y-6">
            {INTRO.map((p, i) => (
              <Reveal key={i} delay={0.06 + i * 0.05} as="p" className="text-base lg:text-lg leading-relaxed" style={{ color: i === 0 ? "#2B2B2B" : "#8C8C8C" }}>
                {p}
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Bios */}
      <section className="py-8 lg:py-12" style={{ backgroundColor: "#F2F1EF" }}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 divide-y" style={{ borderColor: "#D8D8D8" }}>
          {LEADERS.map((l, idx) => (
            <div key={l.name} data-testid={`leader-${l.initials.toLowerCase()}`} className="py-20 lg:py-28">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
                {/* Photo */}
                <div className={`lg:col-span-4 ${idx % 2 === 1 ? "lg:order-2" : ""}`}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.65, ease: EASE }}
                    className="lg:sticky lg:top-28"
                  >
                    <PhotoPlaceholder src={PORTRAITS[l.initials]} alt={`${l.name}, ${l.role}`} initials={l.initials} caption="Portrait" className="w-full" style={{ aspectRatio: "3/4" }} />
                  </motion.div>
                </div>

                {/* Bio */}
                <div className={`lg:col-span-8 ${idx % 2 === 1 ? "lg:order-1" : ""}`}>
                  <Reveal>
                    <span className="h-px w-10 block mb-6" style={{ background: "#F5821F" }} />
                    <h2 className="font-bold leading-[1.1] text-3xl sm:text-4xl" style={{ color: "#2B2B2B" }}>{l.name}</h2>
                    <p className="eyebrow text-xs mt-3 mb-8" style={{ color: "#3B2C82" }}>{l.role}</p>
                  </Reveal>
                  <div className="space-y-5 max-w-3xl">
                    {l.bio.map((p, i) => (
                      <Reveal key={i} delay={i * 0.04} as="p" className="text-base leading-relaxed" style={{ color: "#8C8C8C" }}>
                        {p}
                      </Reveal>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Closing statement band — styled like the homepage "Fortim Way" closing */}
      <section data-testid="leadership-closing" className="relative min-h-[70vh] flex items-center overflow-hidden">
        <img
          src={IMAGES.way}
          alt="Aerial view of a coastal solar farm at dusk"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(31,26,56,0.6)" }} />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 w-full py-24">
          <Reveal className="max-w-4xl">
            <span className="h-px w-12 block mb-8" style={{ background: "#F5821F" }} />
            <p className="text-white font-medium text-2xl sm:text-3xl lg:text-[2.4rem]" style={{ lineHeight: 1.5 }}>
              Fortim Ventures exists to honour a legacy while building for the future. With more than
              a century of regional roots and a new generation bringing global business perspective,
              Fortim combines experience, ambition and long-term thinking to create ventures that
              contribute to the continued growth of DNHDD and beyond.
            </p>
            <button
              data-testid="leadership-contact-btn"
              onClick={() => navigate("/", { state: { scrollTo: "contact" } })}
              className="group mt-10 inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-sm font-semibold text-white transition-colors duration-300"
              style={{ backgroundColor: "#3B2C82" }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#6B5CA5")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#3B2C82")}
            >
              Get in Touch
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </Reveal>
        </div>
      </section>
    </main>
  );
};
